import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { TextWithInfo } from '@openx/components-core/lib/TextWithInfo/TextWithInfo.tsx';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes.ts';
import type { OrganizationData } from '@openx/types/organization.ts';
import { priceFormat } from '@openx/utils/lib/priceFormat.ts';
import { toPercentString } from '@openx/utils/lib/toPercentString.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useTranslation } from 'react-i18next';
import { getOrganizationsLabels, organizationFields } from '../../../constants.ts';
import { isRevenueSharePartnerEnabled } from '../../OrganizationForm/utils.ts';

type OrganizationDetailsProps = {
  organization: OrganizationData;
};

export const OpenAudienceAccessSection = ({ organization }: OrganizationDetailsProps) => {
  const { t } = useTranslation();
  const organizationLabels = getOrganizationsLabels(t);
  const isRevenueSharePartnerSelected = isRevenueSharePartnerEnabled(organization);
  const isPartnerDataFeeSelected = !!organization.exchange_account_partner_id;
  const hasCuratedDealBuyerDiscountsEnabled = !!organization.feature_flags.find(({ value }) => {
    return value === Feature_Flags_Enum.CuratedDealBuyerDiscount;
  });

  const hasFeeSplittingPartnerEnabled = !!organization.feature_flags.find(({ value }) => {
    return value === Feature_Flags_Enum.FeeSplittingPartner;
  });

  return (
    <FormSection divider title={t('3P Data')}>
      <FieldDetails
        toggle
        title={t('Allow access to setting Curated Deal Fees on deals')}
        value={isPartnerDataFeeSelected}
        fullWidth
      />

      {isPartnerDataFeeSelected && (
        <IndentedSections sx={{ mb: 2 }}>
          <FieldDetails
            title={organizationLabels[organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID]}
            value={organization.exchange_account_partner_id}
          />

          <FieldDetails title={organizationLabels[organizationFields.THIRD_PARTY_PARTNER]} value={''} />

          <FieldDetails
            checkbox
            title={organizationLabels[organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED]}
            value={hasCuratedDealBuyerDiscountsEnabled}
          />
        </IndentedSections>
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          mb: 2,
        }}
      >
        <FieldDetails
          customStyles={{ box: { spacingBottom: 0 } }}
          fullWidth={false}
          toggle
          title={t('Enable OpenX Revenue Share Partner')}
          value={isRevenueSharePartnerSelected}
        />

        <Tooltip placement="right" title={t('Curated Deal Fees Data is required for Revenue Share Partners')}>
          <IconButton data-test="tooltip-icon" size="small">
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {isRevenueSharePartnerSelected && (
        <IndentedSections sx={{ mb: 2 }}>
          <Typography sx={{ mb: 1.5 }} variant="h3">
            {t('Organization Max Fees')}
          </Typography>

          <FieldDetails
            title={organizationLabels[organizationFields.MAX_SHARE]}
            value={toPercentString(organization.max_share)}
          />

          <FieldDetails
            title={organizationLabels[organizationFields.MAX_CPM_CAP]}
            value={organization.max_cpm_cap ? priceFormat(organization.max_cpm_cap, 'USD') : null}
          />

          <TextWithInfo
            containerSx={{ mb: 1.5, mt: 1 }}
            tooltip={t('This is the default fee that will be automatically suggested when creating a deal.')}
            variant="h3"
          >
            {t('Organization Default Fees')}
          </TextWithInfo>

          <FieldDetails
            title={organizationLabels[organizationFields.DEFAULT_SHARE]}
            value={toPercentString(organization.default_share)}
          />

          <FieldDetails
            title={organizationLabels[organizationFields.DEFAULT_CPM_CAP]}
            value={organization.default_cpm_cap !== undefined && priceFormat(organization.default_cpm_cap, 'USD')}
          />

          <FieldDetails
            title={organizationLabels[organizationFields.DEFAULT_FEES_DESCRIPTION]}
            value={organization.default_fees_description}
          />

          <Typography sx={{ mb: 1.5, mt: 1 }} variant="h3">
            {t('OpenX Platform Fee')}
          </Typography>

          <FieldDetails
            title={organizationLabels[organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED]}
            value={toPercentString(organization.default_platform_share_ox_sourced)}
          />

          <FieldDetails
            title={organizationLabels[organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED]}
            value={toPercentString(organization.default_platform_share_partner_sourced)}
          />
        </IndentedSections>
      )}

      <FieldDetails
        checkbox
        title={organizationLabels[organizationFields.FEE_SPLITTING_PARTNER]}
        value={hasFeeSplittingPartnerEnabled}
      />
    </FormSection>
  );
};
